// frontend/src/hooks/games/useTicTacToe.ts

import { useCallback, useEffect, useState } from 'react';
import { Player, TicTacToeMode, WIN_LINES } from '../../components/games/tictactoe/tictactoeConstants';
import { pickAiMove } from '../../components/games/tictactoe/ticTacToeAi';

const AI_MARK: Player = 'O';
const HUMAN_MARK: Player = 'X';
const AI_MOVE_DELAY_MS = 550;

export interface TicTacToeScore {
  X: number;
  O: number;
  draws: number;
}

interface RoundResult {
  winner: Player | null;
  line: number[] | null;
}

function checkResult(board: (Player | null)[]): RoundResult {
  for (const line of WIN_LINES) {
    const [a, b, c] = line;
    if (board[a] && board[a] === board[b] && board[a] === board[c]) {
      return { winner: board[a], line };
    }
  }
  return { winner: null, line: null };
}

function emptyBoard(): (Player | null)[] {
  return Array(9).fill(null);
}

export function useTicTacToe(initialMode: TicTacToeMode = 'two-player') {
  const [mode, setModeState] = useState<TicTacToeMode>(initialMode);
  const [board, setBoard] = useState<(Player | null)[]>(() => emptyBoard());
  const [currentPlayer, setCurrentPlayer] = useState<Player>(HUMAN_MARK);
  const [winner, setWinner] = useState<Player | null>(null);
  const [winningLine, setWinningLine] = useState<number[] | null>(null);
  const [score, setScore] = useState<TicTacToeScore>({ X: 0, O: 0, draws: 0 });

  const isDraw = winner === null && board.every((cell) => cell !== null);
  const isRoundOver = winner !== null || isDraw;

  const applyMove = useCallback((index: number, mark: Player) => {
    setBoard((prev) => {
      if (prev[index] !== null) return prev;
      const next = [...prev];
      next[index] = mark;

      const result = checkResult(next);
      if (result.winner) {
        setWinner(result.winner);
        setWinningLine(result.line);
        setScore((s) => ({ ...s, [result.winner as Player]: s[result.winner as Player] + 1 }));
      } else if (next.every((cell) => cell !== null)) {
        setScore((s) => ({ ...s, draws: s.draws + 1 }));
      } else {
        setCurrentPlayer(mark === 'X' ? 'O' : 'X');
      }
      return next;
    });
  }, []);

  const handleCellClick = useCallback((index: number) => {
    if (isRoundOver) return;
    if (mode === 'vs-ai' && currentPlayer === AI_MARK) return; // AI'nin sırasında tıklama yok sayılır
    if (board[index] !== null) return;
    applyMove(index, currentPlayer);
  }, [applyMove, board, currentPlayer, isRoundOver, mode]);

  // Bilgisayara karşı modda, sıra AI'deyse kısa bir gecikmeyle hamlesini yapar.
  useEffect(() => {
    if (mode !== 'vs-ai') return;
    if (isRoundOver) return;
    if (currentPlayer !== AI_MARK) return;

    const timer = setTimeout(() => {
      const aiIndex = pickAiMove(board, AI_MARK, HUMAN_MARK);
      applyMove(aiIndex, AI_MARK);
    }, AI_MOVE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [applyMove, board, currentPlayer, isRoundOver, mode]);

  const startNewRound = useCallback(() => {
    setBoard(emptyBoard());
    setCurrentPlayer(HUMAN_MARK);
    setWinner(null);
    setWinningLine(null);
    // NOT: İleride burada yeni bir "round başladı" XP/istatistik event'i eklenebilir.
  }, []);

  const setMode = useCallback((nextMode: TicTacToeMode) => {
    setModeState(nextMode);
    setBoard(emptyBoard());
    setCurrentPlayer(HUMAN_MARK);
    setWinner(null);
    setWinningLine(null);
  }, []);

  const resetScore = useCallback(() => {
    setScore({ X: 0, O: 0, draws: 0 });
  }, []);

  // NOT: İleride bir round bittiğinde (winner veya isDraw true olduğunda) burada
  // XP/başarım/istatistik event'i tetiklenebilir. Şu an için implemente edilmedi.

  return {
    mode,
    board,
    currentPlayer,
    winner,
    winningLine,
    isDraw,
    isRoundOver,
    score,
    handleCellClick,
    startNewRound,
    setMode,
    resetScore,
  };
}
